import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  CheckCircle as PaidIcon,
  Warning as OverdueIcon,
  Schedule as PendingIcon,
  CreditCard as CardIcon
} from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { StripeCheckoutForm } from '@components/payments/VisitPaymentPanel';
import clientDashboardService from '@services/clientDashboardService';
import EmptyState from '@shared/EmptyState';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

interface Invoice {
  id: string;
  number: string;
  amount_cents: number;
  currency: string;
  status: 'draft' | 'due' | 'paid' | 'overdue' | 'void' | 'partially_paid' | 'unpaid';
  due_date: string;
  issued_at: string;
  paid_at?: string;
  pdf_url?: string;
}

const ClientInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentNotice, setPaymentNotice] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [intentResult, setIntentResult] = useState<{ clientSecret: string; publishableKey?: string } | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<import('@stripe/stripe-js').Stripe | null> | null>(null);
  const [creatingIntent, setCreatingIntent] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, [activeTab]);

  useEffect(() => {
    if (intentResult?.publishableKey) {
      setStripePromise(loadStripe(intentResult.publishableKey));
    }
  }, [intentResult?.publishableKey]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const statusFilter =
        activeTab === 0 ? undefined : activeTab === 1 ? 'paid' : activeTab === 2 ? 'overdue' : undefined;
      const response = await clientDashboardService.getInvoices(statusFilter ? { status: statusFilter } : {});
      let data = Array.isArray(response) ? response : (response as any)?.data || [];
      if (activeTab === 0) {
        data = data.filter((i: Invoice) =>
          ['due', 'overdue', 'partially_paid', 'unpaid'].includes(String(i.status))
        );
      }
      setInvoices(data);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Eroare la incarcarea facturilor');
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setPaymentDialogOpen(true);
  };

  const handlePrepareCardPayment = async () => {
    if (!selectedInvoice) return;
    setCreatingIntent(true);
    setError(null);
    try {
      const data = await clientDashboardService.createInvoicePaymentIntent(selectedInvoice.id);
      setIntentResult({ clientSecret: data.clientSecret, publishableKey: data.publishableKey });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax?.response?.data?.error || 'Eroare la pregătirea plății');
    } finally {
      setCreatingIntent(false);
    }
  };

  const handlePaymentDialogClose = () => {
    setPaymentDialogOpen(false);
    setIntentResult(null);
    setStripePromise(null);
    void loadInvoices();
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const { pdf_url } = await clientDashboardService.getInvoicePDF(invoiceId);
      
      // Descarca PDF-ul
      const link = document.createElement('a');
      link.href = pdf_url;
      link.download = `factura_${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading invoice:', err);
      setError('Eroare la descarcarea facturii');
    }
  };

  const formatAmount = (cents: number, currency: string) => {
    const amount = cents / 100;
    return `${amount.toFixed(2)} ${currency}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'due':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'draft':
        return 'default';
      case 'void':
        return 'default';
      case 'partially_paid':
        return 'warning';
      case 'unpaid':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'paid':
        return 'Platita';
      case 'due':
        return 'De plata';
      case 'overdue':
        return 'Restanta';
      case 'draft':
        return 'Ciorna';
      case 'void':
        return 'Anulata';
      case 'partially_paid':
        return 'Plată parțială';
      case 'unpaid':
        return 'Neplătită';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <PaidIcon fontSize="small" />;
      case 'due':
        return <PendingIcon fontSize="small" />;
      case 'overdue':
        return <OverdueIcon fontSize="small" />;
      default:
        return <ReceiptIcon fontSize="small" />;
    }
  };

  // Calculate totals
  const totalDue = invoices
    .filter(inv =>
      inv.status === 'due' ||
      inv.status === 'overdue' ||
      inv.status === 'partially_paid' ||
      inv.status === 'unpaid'
    )
    .reduce((sum, inv) => sum + inv.amount_cents, 0);
  const totalPaid = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + inv.amount_cents, 0);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          🧾 Facturile Mele
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestioneaza facturile si platile tale
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatAmount(totalDue, 'LEI')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Total de plata
                  </Typography>
                </Box>
                <OverdueIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatAmount(totalPaid, 'LEI')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Total platit
                  </Typography>
                </Box>
                <PaidIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {paymentNotice && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setPaymentNotice(null)}>
          {paymentNotice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab
            icon={<PendingIcon />}
            label="De plata"
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            icon={<PaidIcon />}
            label="Platite"
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            icon={<OverdueIcon />}
            label="Restante"
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
      </Card>

      {/* Empty State */}
      {invoices.length === 0 ? (
        <EmptyState
          icon={<ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary' }} />}
          title="Nu ai facturi"
          description="Facturile tale vor aparea aici"
        />
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Numar Factura</TableCell>
                <TableCell>Data Emiterii</TableCell>
                <TableCell>Scadenta</TableCell>
                <TableCell align="right">Suma</TableCell>
                <TableCell align="right">Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id} hover>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(invoice.status)}
                      label={getStatusLabel(invoice.status)}
                      color={getStatusColor(invoice.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {invoice.number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(invoice.issued_at), 'dd MMM yyyy', { locale: ro })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(invoice.due_date), 'dd MMM yyyy', { locale: ro })}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatAmount(invoice.amount_cents, invoice.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Tooltip title="Descarca PDF">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadInvoice(invoice.id)}
                          sx={{ color: 'primary.main' }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(invoice.status === 'due' ||
                        invoice.status === 'overdue' ||
                        invoice.status === 'partially_paid' ||
                        invoice.status === 'unpaid') && (
                        <Tooltip title="Plateste">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handlePayInvoice(invoice)}
                            startIcon={<PaymentIcon />}
                            sx={{ textTransform: 'none' }}
                          >
                            Plateste
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={handlePaymentDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CardIcon />
            <Typography variant="h6">Plată factură (Stripe)</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Factura:</strong> {selectedInvoice.number}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Total:</strong> {formatAmount(selectedInvoice.amount_cents, selectedInvoice.currency)}
              </Typography>
              {!intentResult && (
                <>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Plata se procesează securizat cu cardul (Stripe). Nu există redirecționare falsă către un PSP inexistent.
                  </Alert>
                  <Button
                    sx={{ mt: 2 }}
                    variant="contained"
                    fullWidth
                    onClick={handlePrepareCardPayment}
                    disabled={creatingIntent}
                    startIcon={<PaymentIcon />}
                  >
                    {creatingIntent ? 'Se pregătește plata…' : 'Continuă cu cardul'}
                  </Button>
                </>
              )}
              {intentResult?.publishableKey && stripePromise && (
                <Box sx={{ mt: 2 }}>
                  <Elements stripe={stripePromise} options={{ clientSecret: intentResult.clientSecret }}>
                    <StripeCheckoutForm
                      clientSecret={intentResult.clientSecret}
                      onOutcome={(o) => {
                        if (o.kind === 'succeeded') {
                          setPaymentDialogOpen(false);
                          setIntentResult(null);
                          setPaymentNotice(null);
                          void loadInvoices();
                          return;
                        }
                        if (o.kind === 'processing') {
                          setPaymentDialogOpen(false);
                          setIntentResult(null);
                          void loadInvoices();
                          setError(null);
                          setPaymentNotice(
                            'Plata este în procesare. Statusul facturii se actualizează după confirmarea Stripe — reveniți în câteva momente.'
                          );
                          return;
                        }
                        setPaymentNotice(null);
                        setError(o.message);
                      }}
                      onCancel={() => setIntentResult(null)}
                    />
                  </Elements>
                </Box>
              )}
              {intentResult && !intentResult.publishableKey && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Lipsește cheia publică Stripe pentru clinică. Contactați recepția sau administratorul.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentDialogClose} disabled={creatingIntent}>
            Închide
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientInvoices;

